import { useEffect, useState } from "react";
import { clsx } from "clsx";
import { Drawer } from "@/components/organisms/Drawer";
import { useUiStore } from "@/store/uiStore";
import { AvailabilityCalendar } from "./AvailabilityCalendar";
import { PropertySlotSettings } from "./PropertySlotSettings";
import type { AvailabilityProperty } from "./AvailabilityDrawer";

interface Props {
  open: boolean;
  property: AvailabilityProperty | null;
  onClose: () => void;
}

// "settings" tab hidden for now — add it back here to re-enable.
const TABS = ["calendar"] as const;
type Tab = "calendar" | "settings";

/**
 * Opens a property's availability from the properties list page (without entering the
 * full property detail drawer). Two tabs: the availability calendar, and the per-property
 * slot settings that apply to all of its generated availability slots.
 */
export function AvailabilityCalendarDrawer({ open, property, onClose }: Props) {
  const { sidebarCollapsed } = useUiStore();
  const [tab, setTab] = useState<Tab>("calendar");

  // Reset to the calendar tab whenever the drawer (re)opens for a property.
  useEffect(() => {
    if (open) setTab("calendar");
  }, [open, property?.id]);

  return (
    <Drawer
      open={open && property != null}
      onClose={onClose}
      onBack={onClose}
      title="Availability"
      subtitle={property?.displayAddress ?? property?.id}
      width="full"
      marginLeft={sidebarCollapsed ? 72 : 240}
    >
      {property && (
        <div className="flex flex-col">
          <div className="flex gap-6 border-b border-bridge-border/60 mb-4">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={clsx(
                  "pb-2 text-sm font-medium border-b-2 transition-colors capitalize",
                  tab === t
                    ? "border-bridge-accent text-bridge-fg"
                    : "border-transparent text-bridge-muted hover:text-bridge-fg",
                )}
              >
                {t}
              </button>
            ))}
          </div>

          {tab === "calendar" ? (
            <AvailabilityCalendar property={property} />
          ) : (
            <PropertySlotSettings property={property} />
          )}
        </div>
      )}
    </Drawer>
  );
}